import { ReactElement, useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import styles from './styles.module.css';

const OVERLAY_FADE_MS = 350;
const CONFETTI_DURATION_MS = 15 * 1000;
const CONFETTI_TAIL_MS = 3000;
const CONFETTI_DEFAULTS = { startVelocity: 15, spread: 360, ticks: 60, zIndex: 10002 };

export function Zsl200Banner({ onClose, visible }: { onClose?: () => void; visible: boolean }): ReactElement {
  const [isClosing, setIsClosing] = useState(false);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const isClosingRef = useRef(false);
  const closedRef = useRef(false);
  const confettiRunningRef = useRef(false);
  const confettiEndRef = useRef<number | null>(null);

  const close = () => {
    if (closedRef.current) return;
    closedRef.current = true;
    onClose?.();
  };

  const stopConfetti = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  useEffect(() => {
    if (!visible) return;
    setIsClosing(false);
    isClosingRef.current = false;
    closedRef.current = false;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    confettiRunningRef.current = false;
    confettiEndRef.current = null;
    const canvas = canvasRef.current;
    if (!canvas) return;

    import('canvas-confetti').then((mod) => {
      if (confettiRunningRef.current) return;

      const fireConfetti = mod.default.create(canvas, { resize: true, useWorker: false });

      confettiRunningRef.current = true;
      if (!confettiEndRef.current) {
        confettiEndRef.current = Date.now() + CONFETTI_DURATION_MS;
      }

      const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;

      intervalRef.current = setInterval(() => {
        const timeLeft = (confettiEndRef.current || 0) - Date.now();

        if (timeLeft <= 0) {
          stopConfetti();
          if (isClosingRef.current) close();
          return;
        }

        const particleCount = 50 * (timeLeft / CONFETTI_DURATION_MS);

        fireConfetti({ ...CONFETTI_DEFAULTS, particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } });
        fireConfetti({ ...CONFETTI_DEFAULTS, particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } });
      }, 250);
    });

    return () => {
      stopConfetti();
    };
  }, [visible]);

  const handleClose = () => {
    if (isClosing) return;

    document.body.style.overflow = '';
    setIsClosing(true);
    isClosingRef.current = true;

    window.setTimeout(() => {
      if (!intervalRef.current) close();
    }, OVERLAY_FADE_MS);

    const closeTailEnd = Date.now() + CONFETTI_TAIL_MS;
    if (!confettiEndRef.current || confettiEndRef.current > closeTailEnd) {
      confettiEndRef.current = closeTailEnd;
    }
  };

  return (
    <div className={`${styles['open-screen']} ${!visible ? styles.hidden : ''} ${isClosing ? styles.closing : ''}`}>
      <canvas ref={canvasRef} className={styles['confetti-canvas']} />
      <div className={`${styles.container} ${isClosing ? styles.closing : ''}`}>
        <div className={styles['container__content--top']}>
          <div className={styles.logo}>
            <Image src="/zsl200-banner/logo.svg" alt="ZSL 200 logo" width={386} height={94} unoptimized />
          </div>
          <div className={styles['container__content--description']}>
            <p>
              ZSL was founded on the 29th April 1826. <strong>It's our Birthday!</strong>
            </p>
          </div>
          <div className={styles['actions']}>
            <div className={styles['btn-wrapper']}>
              <a className={styles['btn']} href="https://www.zsl.org/200">
                Enter the celebrations
              </a>
            </div>
            <div className={styles['btn-wrapper']}>
              <a href='https://www.zsl.org' className={styles['btn']}>
                Continue to ZSL.org
              </a>
            </div>
          </div>
        </div>

        <div className={styles['container__images']}>
          <div className={`${styles.images} ${styles.people}`}>
            <Image src="/zsl200-banner/people.png" alt="" aria-hidden="true" width={1128} height={403} priority />
          </div>
          <div className={`${styles.images} ${styles.yellow}`}>
            <img src="/zsl200-banner/yellow.svg" alt="" aria-hidden="true" />
          </div>
          <div className={`${styles.images} ${styles['middle-man']}`}>
            <Image src="/zsl200-banner/middle-man.png" alt="" aria-hidden="true" width={312} height={321} priority />
          </div>
          <div className={`${styles.images} ${styles.green}`}>
            <img src="/zsl200-banner/green.svg" alt="" aria-hidden="true" />
          </div>
          <div className={`${styles.images} ${styles.base}`}>
            <img src="/zsl200-banner/base.svg" alt="" aria-hidden="true" />
          </div>
        </div>

        <div className={styles['container__content--bottom']}>
          <div className={styles['container__content--description']}>
            <p>
              Thank you for being part of our story
            </p>
          </div>
        </div> 
      </div>
    </div>
  );
}

export default Zsl200Banner;
